import React, { useEffect, useState } from 'react';
import { X, RefreshCw, CheckCircle, AlertCircle, Info } from 'lucide-react';

const Toast = ({
    message,
    onRefresh,
    onClose,
    type = 'info',
    autoClose = 5000,
    position = 'bottom-center',
    showProgress = true
}) => {
    const [isVisible, setIsVisible] = useState(true);
    const [progress, setProgress] = useState(100);

    // Handle auto-close functionality
    useEffect(() => {
        if (!autoClose) return;

        const timer = setInterval(() => {
            setProgress(prev => Math.max(prev - 1, 0));
        }, autoClose / 100);

        return () => clearInterval(timer);
    }, [autoClose]);

    useEffect(() => {
        if (progress <= 0) {
            handleClose();
        }
    }, [progress]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => {
            if (onClose) {
                onClose();
                window.location.reload(true);
            }
        }, 300); // Match this with the transition duration
    };

    const handleRefresh = () => {
        if (onRefresh) onRefresh();
        handleClose();
    };

    // Toast type configurations
    const toastTypes = {
        success: {
            icon: <CheckCircle className="h-5 w-5" />,
            bg: 'bg-emerald-500',
            progress: 'bg-emerald-400',
        },
        error: {
            icon: <AlertCircle className="h-5 w-5" />,
            bg: 'bg-rose-500',
            progress: 'bg-rose-400',
        },
        warning: {
            icon: <AlertCircle className="h-5 w-5" />,
            bg: 'bg-amber-500',
            progress: 'bg-amber-400',
        },
        info: {
            icon: <Info className="h-5 w-5" />,
            bg: 'bg-indigo-500',
            progress: 'bg-indigo-400',
        },
    };

    const currentType = toastTypes[type] || toastTypes.info;

    // Position classes
    const positionClasses = {
        'top-left': 'top-4 left-4',
        'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
        'top-right': 'top-4 right-4',
        'bottom-left': 'bottom-4 left-4',
        'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2',
        'bottom-right': 'bottom-4 right-4',
    };

    if (!isVisible) return null;

    return (
        <div
            className={`fixed ${positionClasses[position]} z-[9999] w-full max-w-xs`}
            role="alert"
            aria-live="assertive"
        >
            <div className={`${currentType.bg} text-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 animate-fade-in-up`}>
                {/* Progress bar */}
                {showProgress && autoClose && (
                    <div className="h-1 w-full bg-black/10">
                        <div
                            className={`h-full ${currentType.progress}`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                )}

                <div className="p-4 flex items-start">
                    <div className="flex-shrink-0 pt-0.5">
                        {currentType.icon}
                    </div>
                    <div className="ml-3 w-0 flex-1">
                        <p className="text-sm font-medium">{message}</p>
                    </div>
                    <div className="ml-4 flex items-center space-x-3">
                        {onRefresh && (
                            <button
                                onClick={handleRefresh}
                                className="rounded-full p-1 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50 transition-colors"
                                aria-label="Refresh"
                            >
                                <RefreshCw className="h-4 w-4" />
                            </button>
                        )}
                        <button
                            onClick={handleClose}
                            className="rounded-full p-1 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50 transition-colors"
                            aria-label="Close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Toast;